/**
 * MUTAGIM.SHOP — Admin Sidebar Navigation
 * Menu latéral pour l'interface d'administration avec support thème clair/sombre
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Import,
  Settings,
  Palette,
  Languages,
  CreditCard,
  Mail,
  BarChart3,
  Shield,
  Search,
  Megaphone,
  Star,
  Image,
  Layers,
  Bell,
  UserCheck,
  Truck,
  Share2,
  Calendar,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Navigation,
  FolderOpen,
  ExternalLink,
  Sparkles,
  Plug,
  Send
} from 'lucide-react';
import { Button } from '../ui/button';

const menuSections = [
  {
    title: 'Principal',
    items: [
      { name: 'Dashboard', icon: LayoutDashboard, path: '/admin', exact: true },
      { name: 'Produits & Import', icon: Package, path: '/admin/sourcing' },
      { name: 'Catégories', icon: FolderOpen, path: '/admin/categories' },
      { name: 'Commandes', icon: ShoppingCart, path: '/admin/orders' },
      { name: 'Avis clients', icon: Star, path: '/admin/reviews/queue' },
    ]
  },
  {
    title: 'Marketing',
    items: [
      { name: 'Promotions', icon: Megaphone, path: '/admin/promotions' },
      { name: 'Newsletter', icon: Mail, path: '/admin/newsletter' },
      { name: 'Social Media', icon: Share2, path: '/admin/social' },
      { name: 'Programme Crew', icon: UserCheck, path: '/admin/crew' },
    ]
  },
  {
    title: 'Apparence',
    items: [
      { name: 'Marque & Logo', icon: Palette, path: '/admin/settings/brand' },
      { name: 'Header & Menu', icon: Navigation, path: '/admin/settings/brand?tab=header' },
      { name: 'Slides Hero', icon: Layers, path: '/admin/slides' },
      { name: 'Bandeau Marques', icon: Image, path: '/admin/brands-marquee' },
      { name: 'Bandeau Promo', icon: Megaphone, path: '/admin/promo-marquee' },
      { name: 'Page Notre Système', icon: FileText, path: '/admin/pages/notre-systeme' },
      { name: 'Atmosphères', icon: Image, path: '/admin/atmospheres' },
      { name: 'Prompts IA', icon: Sparkles, path: '/admin/ai-prompts' },
      { name: 'Traductions', icon: Languages, path: '/admin/translations' },
    ]
  },
  {
    title: 'Analytics',
    items: [
      { name: 'Tableau de bord', icon: BarChart3, path: '/admin/analytics' },
      { name: 'Recherches', icon: Search, path: '/admin/analytics/search' },
      { name: 'Tests A/B', icon: FileText, path: '/admin/analytics/ab-tests' },
      { name: 'Comportement', icon: Users, path: '/admin/analytics/behavior' },
    ]
  },
  {
    title: 'Paramètres',
    items: [
      { name: 'Telegram Scraper', icon: Send, path: '/admin/telegram-scraper' },
      { name: 'Agents Sourcing', icon: Plug, path: '/admin/agents' },
      { name: 'Paiements', icon: CreditCard, path: '/admin/settings/payments' },
      { name: 'Emails', icon: Mail, path: '/admin/settings/emails' },
      { name: 'Notifications', icon: Bell, path: '/admin/notifications' },
      { name: 'Sécurité', icon: Shield, path: '/admin/security/fraud' },
      { name: 'Permissions', icon: UserCheck, path: '/admin/settings/permissions' },
      { name: 'Production', icon: Truck, path: '/admin/settings/production-checklist' },
    ]
  },
  {
    title: 'Aide',
    items: [
      { name: 'Configuration', icon: Settings, path: '/admin/onboarding' },
      { name: 'Documentation', icon: HelpCircle, path: '/admin/help' },
    ]
  }
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminSidebar({ isOpen, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLight } = useAdminTheme();
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  
  // Par défaut : seulement "Principal" et "Aide" sont ouverts
  const [expandedSections, setExpandedSections] = useState(
    menuSections.reduce((acc, section) => ({ 
      ...acc, 
      [section.title]: section.title === 'Principal' || section.title === 'Aide'
    }), {})
  );

  // Charger le logo light depuis les settings
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/api/admin/settings/brand`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Utiliser logo_light pour le sidebar sombre, avec cache buster
          if (data.logo_light) {
            setLogoUrl(`${API_URL}${data.logo_light}?v=${Date.now()}`);
          }
        }
      } catch (err) {
        console.error('Failed to fetch logo:', err);
      }
    };
    
    fetchLogo();
  }, []);

  const toggleSection = (title) => {
    setExpandedSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar - Style corporate moderne (sidebar sombre) */}
      <aside className={cn(
        "fixed left-0 top-0 h-full border-r z-50 transition-all duration-300",
        "w-64 lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        "bg-slate-900 border-slate-800"
      )}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <img 
              src={logoUrl} 
              alt="Mutagim.shop"
              className="h-8 object-contain"
              onError={(e) => { e.target.src = '/logo.png'; }}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={onToggle}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Storefront Link */}
        <div className="px-3 py-3 border-b border-slate-800">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 text-indigo-300 hover:text-white hover:border-indigo-400 transition-all group"
            data-testid="nav-view-storefront"
          >
            <ExternalLink className="w-5 h-5 text-indigo-400 group-hover:text-indigo-300" />
            <span>Voir la boutique</span>
            <ExternalLink className="w-4 h-4 ml-auto opacity-50 group-hover:opacity-100" />
          </a>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {menuSections.map((section) => (
            <div key={section.title} className="mb-4">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
              >
                {section.title}
                {expandedSections[section.title] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Section Items */}
              {expandedSections[section.title] && (
                <div className="space-y-1 mt-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path, item.exact);
                    
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          if (window.innerWidth < 1024) onToggle?.();
                        }}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
                          active 
                            ? "bg-indigo-600 text-white font-medium shadow-lg shadow-indigo-600/20"
                            : "text-slate-400 hover:text-white hover:bg-slate-800"
                        )}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={cn(
                          "w-5 h-5 flex-shrink-0",
                          active ? "text-white" : "text-slate-500"
                        )} />
                        <span>{item.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

// Mobile toggle button
export function AdminSidebarToggle({ onClick }) {
  const { isLight } = useAdminTheme();
  
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className={cn(
        "lg:hidden",
        isLight ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"
      )}
      data-testid="sidebar-toggle"
    >
      <Menu className="w-6 h-6" />
    </Button>
  );
}

export default AdminSidebar;
